// Banner (alerts pinned under the shell bar, or inline), Stamp (the big outcome word) and Curtain
// (the stamp across the whole window when a room ends).
import { useEffect, useState, type HTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { afterMotion } from './Motion';

export function Banner({ tone = 'error', inline, mono, children, className = '', ...rest }: { tone?: 'error' | 'warn' | 'info'; inline?: boolean; mono?: boolean; children: ReactNode; className?: string } & HTMLAttributes<HTMLDivElement>) {
  return <div className={`fk-banner fk-hit ${tone !== 'error' ? `fk-banner--${tone}` : ''} ${inline ? 'fk-banner--inline' : ''} ${mono ? 'fk-banner--mono' : ''} ${className}`} role="alert" {...rest}>{children}</div>;
}

export type StampTone = 'victory' | 'defeat' | 'neutral';

/** VICTORY / DEFEAT / FLED … stamped onto the screen with a settle. */
export function Stamp({ tone = 'victory', sub, small, children, className = '', ...rest }: { tone?: StampTone; sub?: ReactNode; small?: boolean; children: ReactNode; className?: string } & HTMLAttributes<HTMLDivElement>) {
  return <div className={`fk-stamp ${tone !== 'victory' ? `fk-stamp--${tone}` : ''} ${small ? 'fk-stamp--sm' : ''} ${className}`} role="status" {...rest}><span>{children}</span>{sub && <span className="fk-stamp__sub">{sub}</span>}</div>;
}

/**
 * The room's result across the whole window (portalled to <body>, like Modal): a vignette, rays and
 * the Stamp, then a fade. It never takes clicks (the next screen is already live underneath) and
 * calls `onDone` once it has faded.
 */
export function Curtain({ tone = 'victory', eyebrow, word, sub, holdMs = 2200, onDone }: { tone?: StampTone; eyebrow?: ReactNode; word: ReactNode; sub?: ReactNode; holdMs?: number; onDone?: () => void }) {
  const [leaving, setLeaving] = useState(false);
  useEffect(() => afterMotion(holdMs, () => setLeaving(true)), [holdMs]);
  useEffect(() => (leaving ? afterMotion(420, () => onDone?.()) : undefined), [leaving, onDone]);
  return createPortal(
    <div className={`fk-curtain fk-curtain--${tone} ${leaving ? 'fk-curtain--leaving' : ''}`} aria-live="assertive" data-testid="outcome-curtain" data-tone={tone}>
      <div className="fk-curtain__rays" aria-hidden="true" />
      <div className="fk-curtain__body">
        {eyebrow && <span className="fk-eyebrow fk-curtain__eyebrow">{eyebrow}</span>}
        <Stamp tone={tone} sub={sub}>{word}</Stamp>
      </div>
    </div>,
    document.body,
  );
}
